'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useCallback, useMemo, type ReactNode } from 'react'

import { HeroBannerDS, hasAuthoredVerticalMargins, toHeroDSRichTextItems } from '@src/components/blocks/hero-banner-ds'
import type { IProductCard } from '@src/components/product-card/product-card.types'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Carousel, CarouselContent, CarouselItem } from '@src/ds/blocks/ds-carousel'
import { ProductCard } from '@src/ds/blocks/product-card'
import { SectionContentWrapper } from '@src/ds/blocks/section-content-wrapper'
import { useContentWithBackgroundImageProps } from '@src/quince-components/src'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { getValidPromo } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { SectionProps } from '@src/SectionRenderer/SectionRenderer.types'
import styles from '@src/SectionRenderer/sections/halfBannerHalfProductTiles/halfBannerHalfProductTiles.module.scss'
import { toHeroDSContainerStyles } from '@src/SectionRenderer/sections/heroBanner/hero-banner-ds-styles'
// Shared hero-banner overlay positioning (bottom-anchored, left-aligned) — the same classes the
// heroBannerV2 DS adapter applies, so the banner here matches the legacy `<HeroBannerV2>` it renders.
import {
  heroV2ButtonWrapperClass,
  heroV2ContentClass,
  heroV2ContentInnerClass,
  heroV2ContentInnerGapClass,
  heroV2WrapperClass,
} from '@src/SectionRenderer/sections/heroBannerV2/hero-banner-v2-ds-styles'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { getFindingMethodAggregate } from '@src/telemetry/findingMethod'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

/** Legacy parity: only the first 6 product cards render, regardless of how many the CMS sends. */
export const MAX_PRODUCTS_TO_SHOW = 6

/** Legacy parity: per-tile tracker suffix. */
export const TRACKER_SUFFIX = '__product_category_carousel'

/** Legacy parity: banner baseDynamicClass injected for Contentful theming. */
const BASE_DYNAMIC_HEIGHT = 'height--595'

/* eslint-disable i18next/no-literal-string -- Panda token ids + CSS keywords, not user copy */
// COUPLED TO THE MOBILE BLEED: `carouselViewportCss` below cancels exactly this `px: {base:'0'}` plus
// `carouselWrapperClass`'s `pl: 'space-16'`, so the bleed assumes NOTHING else adds horizontal padding
// here. Both consumers join a Contentful-derived `useDynamicStyle` class onto this same element, so a CMS
// class that re-introduces horizontal padding leaves a residual inset on one side while the other still
// bleeds — and if it lands on an ancestor instead, the row spills past the band into horizontal page
// scroll. If a section ever needs authored horizontal padding, the bleed offsets have to move with it.
// Width and the `bp-desktop-cap` background cap come from `SectionContentWrapper`.
export const containerClass = css({
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: 'paperwhite',
  px: { base: '0', lg: 'space-48' },
})

export const innerContainerClass = css({
  width: 'full',
  maxWidth: '1504px',
  display: 'flex',
  gap: { base: 'space-16', lg: 'space-24' },
  flexDirection: { base: 'column', lg: 'row' },
  // Desktop: stretch product column to banner `height--595` (legacy sparse-grid empty space).
  alignItems: 'stretch',
  py: { base: '0', lg: 'space-64' },
})

export const bannerSlotClass = css({
  width: { base: 'full', lg: '50%' },
  // Mobile square; desktop height from hero `height--595`.
  aspectRatio: { base: '1 / 1', lg: 'auto' },
  position: 'relative',
  overflow: 'hidden',
})

const bannerFillClass = css({ height: 'full', width: 'full' })
// Cover-fill media; container height owns the desktop row (not the product grid).
const bannerImageCoverClass = css({
  position: 'absolute',
  inset: '0',
  width: 'full',
  height: 'full!',
  objectFit: 'cover',
} as SystemStyleObject)

export const productsSlotClass = css({
  width: { base: 'full', lg: '50%' },
})

const gridClass = css({
  display: 'grid',
  width: 'full',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: 'space-24',
  rowGap: 'space-48',
})

const carouselWrapperClass = css({
  width: 'full',
  containerType: 'inline-size',
  pb: 'space-32',
  // Load-bearing beyond the visual inset: `26cqi` below resolves against this element's CONTENT box, so
  // this padding is part of the card-width formula. The full-bleed below therefore moves the VIEWPORT
  // rather than dropping this padding — widening the query container would grow every card by ~4px.
  pl: 'space-16',
  '& [data-slot="carousel-content"]': {
    gap: 'space-8',
  },
  '& [data-slot="carousel-item"]': {
    minWidth: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '26cqi',
  },
  // Breathing room at both ends of the full-bleed track (see `carouselViewportCss`): the edge cards
  // still rest on the 16px inset the banner above bleeds past, while the track itself runs to both
  // screen edges mid-scroll. Margins on the edge slides, not padding on the track — with
  // `containScroll: 'trimSnaps'` the first slide's snap clamps to 0, which is what preserves the
  // leading margin as the at-rest inset. Reset at `lg`, where this column is a contained 50% half.
  '& [data-slot="carousel-item"]:first-child': { marginInlineStart: { base: 'space-16', lg: '0' } },
  '& [data-slot="carousel-item"]:last-child': { marginInlineEnd: { base: 'space-16', lg: '0' } },
})

// Full-bleed on BOTH sides: the section band already drops its horizontal padding below `lg`
// (`containerClass` px `base: '0'`), so the only thing insetting the row is this wrapper's `pl`. Pull the
// carousel VIEWPORT back over it and add that 16px to the width, and the viewport spans exactly 100vw —
// cards scroll off both screen edges instead of being clipped at a gutter on the left.
//
// Applied to the viewport, not the wrapper, for two reasons: the wrapper's padding is part of the `26cqi`
// card-width formula (above), and the viewport is where `overflow: hidden` lives — the clip edge is what
// actually has to move for the row to bleed.
//
// Merged by web-ui into the viewport's own `css()` call (`css({overflow, minWidth, width}, viewportCss)`),
// so this `width` deterministically beats the slot's default `width: 'full'`.
//
// Confined to `base` on purpose: at `lg` the band takes its `space-48` padding back and this carousel
// sits in a contained 50% column beside the banner, where a leftward bleed would just spill 16px into the
// column gap. `useDeviceInfo` treats tablets as non-desktop, so that combination does render.
const carouselViewportCss = css.raw({
  width: { base: 'calc(100% + var(--web-ui-spacing-space-16))', lg: 'full' },
  marginInlineStart: { base: 'calc(var(--web-ui-spacing-space-16) * -1)', lg: '0' },
})
/* eslint-enable i18next/no-literal-string */

export const ProductTileGrid = ({ tiles }: { tiles: ReactNode[] }) => (
  <div className={gridClass} data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT_TILE_GRID}>
    {tiles}
  </div>
)

export const ProductTileCarousel = ({ tiles }: { tiles: ReactNode[] }) => {
  const opts = useMemo(
    () =>
      ({
        align: 'start',
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
        dragFree: true,
      }) as const,
    [],
  )
  return (
    <div
      className={carouselWrapperClass}
      data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT_TILE_CAROUSEL}
    >
      <Carousel opts={opts}>
        <CarouselContent viewportCss={carouselViewportCss}>
          {tiles.map((tile, idx) => (
            <CarouselItem key={idx}>{tile}</CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  )
}

/** Banner half: HeroBannerDS + shared overlays + desktop `height--595`. Exported for the PL nested widget. */
export const HalfBannerBannerDs = ({
  sectionConfig,
  tracker,
}: {
  sectionConfig: SectionProps['sectionConfig']
  tracker: string
}) => {
  const { id, trackingMetaData, sectionType, sectionIndex } = sectionConfig

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const enrichedTrackingMetaData = useMemo(
    () => ({
      context: {
        ...trackingMetaData?.context,
        col: sectionIndex,
        sectionId: id,
        sectionType,
      },
    }),
    [trackingMetaData, sectionIndex, id, sectionType],
  )

  const bannerSectionConfig = useMemo(
    () => ({ ...sectionConfig, tracker, baseDynamicClasses: [BASE_DYNAMIC_HEIGHT] }),
    [sectionConfig, tracker],
  )

  const contentWithBackgroundImageProps = useContentWithBackgroundImageProps(bannerSectionConfig)

  // Desktop `height--595` as minHeight on styleObject (HeroBannerDS spreads styleObject last).
  const desktopBannerHeightStyle = useMemo(() => {
    if (!isDesktop) return undefined
    const sizing = toHeroDSContainerStyles([BASE_DYNAMIC_HEIGHT])
    if (!sizing?.height) return sizing
    const { height, ...rest } = sizing
    return { ...rest, minHeight: height }
  }, [isDesktop])

  const heroBannerProps = useMemo(
    () => ({
      ...contentWithBackgroundImageProps,
      styleObject: {
        ...contentWithBackgroundImageProps.styleObject,
        ...desktopBannerHeightStyle,
      },
      richTextsItems: toHeroDSRichTextItems(contentWithBackgroundImageProps.richTextsItems),
      imageProps: {
        ...contentWithBackgroundImageProps.imageProps,
        // @i18n-ignore
        loading: 'eager' as const,
        url: contentWithBackgroundImageProps.imageProps?.url ?? '',
        title: contentWithBackgroundImageProps.imageProps?.title ?? '',
        className: [bannerImageCoverClass, contentWithBackgroundImageProps.imageProps?.className]
          .filter(Boolean)
          .join(' '),
      },
      ...(contentWithBackgroundImageProps.videoProps && {
        videoProps: {
          ...contentWithBackgroundImageProps.videoProps,
          className: [bannerImageCoverClass, contentWithBackgroundImageProps.videoProps.className]
            .filter(Boolean)
            .join(' '),
        },
      }),
      tracker,
      trackingMetaData: enrichedTrackingMetaData,
      overrideMaxImageWidth1600: isDesktop,
      fireImageClickEventAlways: true,
      deviceType,
    }),
    [
      contentWithBackgroundImageProps,
      desktopBannerHeightStyle,
      tracker,
      enrichedTrackingMetaData,
      isDesktop,
      deviceType,
    ],
  )

  return (
    <HeroBannerDS
      {...heroBannerProps}
      className={[heroV2WrapperClass, bannerFillClass, heroBannerProps.className].filter(Boolean).join(' ')}
      contentClassName={heroV2ContentClass}
      contentInnerWrapper={[
        heroV2ContentInnerClass,
        // Authored vertical margins own the overlay's spacing; the Figma gap fills in only when absent.
        !hasAuthoredVerticalMargins(contentWithBackgroundImageProps.richTextsItems) && heroV2ContentInnerGapClass,
      ]
        .filter(Boolean)
        .join(' ')}
      buttonsWrapperClassName={heroV2ButtonWrapperClass}
    />
  )
}

/**
 * DS implementation of the `halfBannerHalfProductTiles` home-page section.
 *
 * Replaces the legacy SCSS/Swiper UI with `HeroBannerDS` + a mini-variant
 * `ProductCard` grid (desktop) / carousel (mobile). Selection between this
 * widget and the legacy implementation is owned by `HalfBannerHalfProductTiles.tsx`
 * via `shouldUseDesignSystem()`.
 */
const HalfBannerHalfProductTilesWidgetDs = ({ sectionConfig }: SectionProps) => {
  // Hoisted, not called per card: the wrapper around this hook writes localStorage + Datadog context on
  // every call, so a per-tile call multiplies that by the grid size on each render.
  const isSvdEnabled = useIsSvdEnabled()
  const {
    tracker: baseTracker,
    classNames = [],
    classNamesMobileOverrides = [],
    baseDynamicClasses = [],
    mobileDynamicClassesOverrides = [],
    id,
    trackingMetaData,
    sectionType,
    productCards = [],
  } = sectionConfig

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const { className: wrapperClassName, styleObject: wrapperStyle } = useDynamicStyle({
    mobileClassNamesOverride: classNamesMobileOverrides,
    baseClassNames: classNames,
    baseDynamicValueClasses: baseDynamicClasses,
    dynamicValueClassesMobileOverride: mobileDynamicClassesOverrides,
    postFix: styles.dynamicClassesPostFix ?? '',
  })

  // Legacy parity: `${tracker}__product_category_carousel` is built unconditionally —
  // when `baseTracker` is undefined the string is `"undefined__product_category_carousel"`.
  // Preserved verbatim so existing dashboards keep receiving the same tracker value.
  const tracker = `${baseTracker}${TRACKER_SUFFIX}`

  // Legacy parity (half-banner-half-product-tiles-legacy.tsx): the SVD branch initializes the
  // batched BULK_PRODUCT_CARD_VIEW stream and threads `productViewTracker` into SvdProductCard;
  // without the init, card impressions are silently dropped (STRFRNT-16871). The non-SVD DS card
  // deliberately stays untracked — legacy never had impressions on that branch either.
  const productViewTracker = `${tracker}__product_view`
  useBatchedAnalyticsForProductCard({ productViewTracker, trackingMetaData })

  const getIsVisibleTrackParams = useCallback(() => ({ ...trackingMetaData }), [trackingMetaData])
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const contentContainerTrackingMetadata = useMemo(
    () => getContentContainerTrackingMetadata(trackingMetaData),
    [trackingMetaData],
  )

  const products = useMemo(() => productCards.slice(0, MAX_PRODUCTS_TO_SHOW), [productCards])

  const renderTile = useCallback(
    (productCard: IProductCard, idx: number) => {
      const trackingContext = {
        ...getTrackingContext(tracker, productCard.product.slug),
        tracker,
      }
      const validPromo = getValidPromo(productCard.product.cardVariants[0]?.promoConfig)

      const productClickTrackConfig = {
        trackInternal: true,
        trackGA: true,
        ...trackingContext,
        context: {
          sectionType,
          sectionid: id,
          ...trackingMetaData?.context,
          productPosition: idx,
          ...contentContainerTrackingMetadata,
          findingMethod: getFindingMethodAggregate(),
          is_final_sale: validPromo?.isFinalSale ? 1 : 0,
          // `displayConfig?.`, not `displayConfig.`: this config is built above the SVD branch and used by
          // BOTH, and `displayConfig` is `null` at runtime for a no-VDA SVD card. The legacy `CardVariant`
          // type declares it non-nullable, so TS never asks for the second `?.` — the same type-lie-vs-wire
          // -shape mismatch already fixed in the two sibling sections.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- legacy CardVariant types displayConfig as non-nullable; SVD data has it null at runtime
          is_app_exclusive: productCard.product.cardVariants[0]?.displayConfig?.appExclusive ? 1 : 0,
          productProperties: {
            discounted_price: validPromo?.promoPrice ?? productCard.product.cardVariants[0]?.price,
            price: productCard.product.cardVariants[0]?.price,
          },
        },
      }

      const wishlistTrackConfig = {
        ...trackingContext,
        sectionType,
        sectionid: id,
        ...trackingMetaData?.context,
        productPosition: idx,
        ...contentContainerTrackingMetadata,
        findingMethod: getFindingMethodAggregate(),
      }

      // TEMPORARY (STRFRNT-15810): `isPlSvdCard` shape-guards on anchor data, so a legacy-shaped card
      // falls through to the DS card below.
      if (isSvdEnabled && isPlSvdCard(productCard.product)) {
        return (
          <SvdProductCard
            key={`svd_${String(productCard.product.productId)}`}
            product={mapPlSvdCard(productCard.product as unknown as PlSvdCard)}
            dsVariant="mini"
            tracker={tracker}
            productViewTracker={productViewTracker}
            hideWishlist={!isDesktop}
            hideBuyNow={!isDesktop}
            hideTitle
            hideTags
            priceCompareAtFirst
            hideSubscriptionPrice
            productClickTrackConfig={productClickTrackConfig}
            wishlistTrackingConfig={wishlistTrackConfig}
          />
        )
      }
      return (
        <ProductCard
          key={`${productCard.product.productId}`}
          data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT(idx)}
          productCard={productCard}
          dsVariant="mini"
          tracker={tracker}
          hideWishlistIcon={!isDesktop}
          hideBuyNowIcon={!isDesktop}
          hideTitle
          hideTags
          hideSubscriptionPrice
          priceCompareAtFirst
          productClickTrackConfig={productClickTrackConfig}
          wishlistTrackConfig={wishlistTrackConfig}
        />
      )
    },
    [
      tracker,
      productViewTracker,
      sectionType,
      id,
      trackingMetaData,
      contentContainerTrackingMetadata,
      isDesktop,
      isSvdEnabled,
    ],
  )

  const tiles = useMemo(() => products.map(renderTile), [products, renderTile])

  return (
    <SectionContentWrapper
      ref={trackIsVisibleRef}
      className={[containerClass, wrapperClassName].filter(Boolean).join(' ')}
      style={wrapperStyle}
      data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.ROOT}
    >
      <div className={innerContainerClass}>
        <div className={bannerSlotClass}>
          <HalfBannerBannerDs sectionConfig={sectionConfig} tracker={tracker} />
        </div>
        <div className={productsSlotClass}>
          {isDesktop ? <ProductTileGrid tiles={tiles} /> : <ProductTileCarousel tiles={tiles} />}
        </div>
      </div>
    </SectionContentWrapper>
  )
}

export default HalfBannerHalfProductTilesWidgetDs
